// KitchenStockManagement.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faTrash,
  faTriangleExclamation,
  faClockRotateLeft,
  faBoxesPacking,
  faChevronDown,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { Alert, Badge } from 'react-bootstrap';

import { getUserInfo } from '../../../../../services/apiClient';
import { useKitchenStock } from './useKitchenStock';
import ReceiptModal from './ReceiptModal';
import WriteOffModal from './WriteOffModal';
import './kitchen-stock.css';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('uk-UA') : '—';

const daysUntil = (d) => {
  if (!d) return null;
  const diff = new Date(d).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0);
  return Math.round(diff / (1000 * 60 * 60 * 24));
};

const expiryBadge = (expiryDate) => {
  if (!expiryDate) return null;
  const days = daysUntil(expiryDate);
  let variant = 'secondary';
  if (days <= 0) variant = 'dark';
  else if (days <= 1) variant = 'danger';
  else if (days <= 3) variant = 'warning';
  return <Badge bg={variant}>{fmtDate(expiryDate)}{days >= 0 ? ` (${days} дн.)` : ' (прострочено)'}</Badge>;
};

const movementTypeLabel = {
  receipt: { label: 'Надходження', variant: 'success' },
  consumption: { label: 'Витрата на меню', variant: 'primary' },
  writeoff: { label: 'Списання', variant: 'danger' },
  correction: { label: 'Коригування', variant: 'secondary' }
};

const statusLabel = {
  active: { label: 'Активна', variant: 'success' },
  depleted: { label: 'Вичерпана', variant: 'secondary' },
  expired: { label: 'Прострочена', variant: 'dark' },
  written_off: { label: 'Списана', variant: 'danger' }
};

const KitchenStockManagement = () => {
  const kindergartenId = useMemo(() => {
    const info = getUserInfo();
    return info && info.privilegedUser ? info.privilegedUser.area : null;
  }, []);

  const [activeTab, setActiveTab] = useState('balance');
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [isWriteOffOpen, setIsWriteOffOpen] = useState(false);
  const [movementFilter, setMovementFilter] = useState({ type: '', dateFrom: '', dateTo: '' });
  const [batchStatusFilter, setBatchStatusFilter] = useState('');
  const [expandedBaseIds, setExpandedBaseIds] = useState({});

  const toggleExpanded = (baseId) => setExpandedBaseIds(prev => ({ ...prev, [baseId]: !prev[baseId] }));

  const { balance, batches, movements, expiring, loading, error, actions } = useKitchenStock(kindergartenId);

  useEffect(() => {
    if (!kindergartenId) return;
    if (activeTab === 'balance') actions.loadBalance();
    if (activeTab === 'expiring') actions.loadExpiring(5);
    if (activeTab === 'batches') actions.loadBatches({ status: batchStatusFilter });
    if (activeTab === 'movements') actions.loadMovements(movementFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, kindergartenId]);

  useEffect(() => {
    if (activeTab === 'batches' && kindergartenId) actions.loadBatches({ status: batchStatusFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchStatusFilter]);

  const applyMovementFilter = () => actions.loadMovements(movementFilter);

  const refreshCurrentTab = () => {
    if (activeTab === 'balance') actions.loadBalance();
    if (activeTab === 'expiring') actions.loadExpiring(5);
    if (activeTab === 'batches') actions.loadBatches({ status: batchStatusFilter });
    if (activeTab === 'movements') actions.loadMovements(movementFilter);
  };

  const handleReceiptSubmit = async (data) => {
    await actions.receive(data);
    actions.loadBalance();
    refreshCurrentTab();
  };

  const handleWriteOffSubmit = async (data) => {
    await actions.writeOff(data);
    actions.loadBalance();
    refreshCurrentTab();
  };

  const tabs = [
    { id: 'balance', label: 'Залишки', icon: faBoxesPacking },
    { id: 'expiring', label: 'Спливає термін', icon: faTriangleExclamation, badge: expiring.length },
    { id: 'batches', label: 'Партії', icon: faClockRotateLeft },
    { id: 'movements', label: 'Журнал руху', icon: faClockRotateLeft }
  ];

  if (!kindergartenId) {
    return <Alert variant="warning">Не вдалося визначити заклад для перегляду складу.</Alert>;
  }

  return (
    <div className="card queuetable-table-card kitchen-stock-card">
      <div className="card-body p-0">
        <div className="queuetable-year-bar d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <span className="queuetable-label text-white-50">Склад</span>
            <ul className="queuetable-year-tabs nav nav-tabs">
              {tabs.map(tab => (
                <li key={tab.id} className="nav-item">
                  <button
                    className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <FontAwesomeIcon icon={tab.icon} className="me-2" />
                    {tab.label}
                    {!!tab.badge && <Badge bg="danger" className="ms-2">{tab.badge}</Badge>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-sm btn-primary" onClick={() => setIsReceiptOpen(true)}>
              <FontAwesomeIcon icon={faPlus} className="me-1" />
              Оприбуткувати
            </button>
            <button className="btn btn-sm btn-outline-danger" onClick={() => setIsWriteOffOpen(true)}>
              <FontAwesomeIcon icon={faTrash} className="me-1" />
              Списати
            </button>
          </div>
        </div>

        {error && <Alert variant="danger" className="m-3">{error}</Alert>}

        <div className="kitchen-stock-content p-3">
          {activeTab === 'balance' && (
            <div className="table-responsive">
              <table className="table queuetable-table align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '2rem' }}></th>
                    <th>Продукт (базовий)</th>
                    <th>Одиниця</th>
                    <th>Залишок, всього</th>
                    <th>Найближчий термін придатності</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} className="text-center py-4">Завантаження...</td></tr>
                  ) : balance.length > 0 ? balance.map(row => {
                    const hasVariants = row.variants && row.variants.length > 1;
                    const isExpanded = !!expandedBaseIds[row._id];
                    return (
                      <React.Fragment key={row._id}>
                        <tr
                          className={hasVariants ? 'kitchen-stock-clickable' : ''}
                          onClick={() => hasVariants && toggleExpanded(row._id)}
                        >
                          <td>
                            {hasVariants && (
                              <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
                            )}
                          </td>
                          <td>{row.baseProductName}</td>
                          <td>{row.unit}</td>
                          <td>{row.quantityRemaining}</td>
                          <td>{row.nearestExpiry ? expiryBadge(row.nearestExpiry) : '—'}</td>
                        </tr>
                        {hasVariants && isExpanded && row.variants.map(v => (
                          <tr key={v.productId} className="kitchen-stock-variant-row">
                            <td></td>
                            <td className="text-muted ps-4">— {v.productName}</td>
                            <td>{v.unit}</td>
                            <td>
                              {v.quantityRemaining}
                              {v.packageSize > 0 && (
                                <span className="text-muted small ms-1">
                                  (≈{Math.floor(v.quantityRemaining / v.packageSize)} шт)
                                </span>
                              )}
                            </td>
                            <td>{v.nearestExpiry ? expiryBadge(v.nearestExpiry) : '—'}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  }) : (
                    <tr><td colSpan={5} className="text-center py-4 text-muted">На складі порожньо. Почніть з оприбуткування.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'expiring' && (
            <div className="table-responsive">
              <table className="table queuetable-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Продукт</th>
                    <th>Залишок партії</th>
                    <th>Термін придатності</th>
                    <th>Постачальник</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={4} className="text-center py-4">Завантаження...</td></tr>
                  ) : expiring.length > 0 ? expiring.map(batch => (
                    <tr key={batch._id}>
                      <td>{batch.productName}</td>
                      <td>{batch.quantityRemaining} {batch.unit}</td>
                      <td>{expiryBadge(batch.expiryDate)}</td>
                      <td>{batch.supplier || '—'}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={4} className="text-center py-4 text-muted">Найближчим часом нічого не спливає.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'batches' && (
            <>
              <div className="d-flex align-items-center gap-2 mb-3">
                <span className="text-muted small">Статус:</span>
                <select className="form-select form-select-sm w-auto" value={batchStatusFilter} onChange={(e) => setBatchStatusFilter(e.target.value)}>
                  <option value="">Всі</option>
                  <option value="active">Активні</option>
                  <option value="depleted">Вичерпані</option>
                  <option value="written_off">Списані</option>
                </select>
              </div>
              <div className="table-responsive">
                <table className="table queuetable-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Продукт</th>
                      <th>Надійшло</th>
                      <th>Залишок</th>
                      <th>Дата надходження</th>
                      <th>Термін придатності</th>
                      <th>№ накладної</th>
                      <th>Постачальник</th>
                      <th>Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={8} className="text-center py-4">Завантаження...</td></tr>
                    ) : batches.length > 0 ? batches.map(b => (
                      <tr key={b._id}>
                        <td>{b.productName}</td>
                        <td>{b.quantityReceived} {b.unit}</td>
                        <td>
                          {b.quantityRemaining} {b.unit}
                          {b.packageSize > 0 && (
                            <span className="text-muted small ms-1">
                              (≈{Math.floor(b.quantityRemaining / b.packageSize)} шт)
                            </span>
                          )}
                        </td>
                        <td>{fmtDate(b.receivedDate)}</td>
                        <td>{b.expiryDate ? fmtDate(b.expiryDate) : '—'}</td>
                        <td>{b.documentNumber || '—'}</td>
                        <td>{b.supplier || '—'}</td>
                        <td><Badge bg={(statusLabel[b.status] || {}).variant || 'secondary'}>{(statusLabel[b.status] || {}).label || b.status}</Badge></td>
                      </tr>
                    )) : (
                      <tr><td colSpan={8} className="text-center py-4 text-muted">Партій не знайдено.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {activeTab === 'movements' && (
            <>
              <div className="d-flex align-items-end gap-2 mb-3 flex-wrap">
                <div>
                  <label className="form-label small text-muted mb-1">Тип руху</label>
                  <select
                    className="form-select form-select-sm"
                    value={movementFilter.type}
                    onChange={(e) => setMovementFilter(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <option value="">Всі</option>
                    <option value="receipt">Надходження</option>
                    <option value="consumption">Витрата на меню</option>
                    <option value="writeoff">Списання</option>
                    <option value="correction">Коригування</option>
                  </select>
                </div>
                <div>
                  <label className="form-label small text-muted mb-1">З дати</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={movementFilter.dateFrom}
                    onChange={(e) => setMovementFilter(prev => ({ ...prev, dateFrom: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label small text-muted mb-1">По дату</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={movementFilter.dateTo}
                    onChange={(e) => setMovementFilter(prev => ({ ...prev, dateTo: e.target.value }))}
                  />
                </div>
                <button className="btn btn-sm btn-outline-primary" onClick={applyMovementFilter}>Застосувати</button>
              </div>

              <div className="table-responsive">
                <table className="table queuetable-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Дата</th>
                      <th>Тип</th>
                      <th>Продукт</th>
                      <th>Кількість</th>
                      <th>Причина</th>
                      <th>Хто</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={6} className="text-center py-4">Завантаження...</td></tr>
                    ) : movements.length > 0 ? movements.map(m => (
                      <tr key={m._id}>
                        <td>{fmtDate(m.date)}</td>
                        <td><Badge bg={(movementTypeLabel[m.type] || {}).variant || 'secondary'}>{(movementTypeLabel[m.type] || {}).label || m.type}</Badge></td>
                        <td>{m.productName || m.productId}</td>
                        <td>{m.quantity} {m.unit}</td>
                        <td>{m.reason || '—'}</td>
                        <td>{m.createdBy}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={6} className="text-center py-4 text-muted">Записів не знайдено.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <ReceiptModal
        isOpen={isReceiptOpen}
        onClose={() => setIsReceiptOpen(false)}
        onSubmit={handleReceiptSubmit}
      />
      <WriteOffModal
        isOpen={isWriteOffOpen}
        onClose={() => setIsWriteOffOpen(false)}
        onSubmit={handleWriteOffSubmit}
        balance={balance}
      />
    </div>
  );
};

export default KitchenStockManagement;